import React, { useEffect, useState } from 'react';
import Paper                          from '@mui/material/Paper';
import Table                          from '@mui/material/Table';
import TableBody                      from '@mui/material/TableBody';
import TableContainer                 from '@mui/material/TableContainer';
import TableHead                      from '@mui/material/TableHead';
import TablePagination                from '@mui/material/TablePagination';
import TableRow                       from '@mui/material/TableRow';
import TableSortLabel                 from '@mui/material/TableSortLabel';
import { NoDataFound }                from './NoDataFound';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import countries                      from 'i18n-iso-countries';
import en                             from 'i18n-iso-countries/langs/en.json';
import {
  StaticticsColumWrapper,
  StatisticsTableCellWrapper,
}                                     from '../../../Auth/ChangeSubscription/styles';


countries.registerLocale(en);

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right';
}

const columnsAdmin: readonly Column[] = [
  { id: 'firstName', label: 'First Name', minWidth: 100 },
  { id: 'lastName', label: 'Last Name', minWidth: 100 },
  { id: 'middleName', label: 'Middle Name', minWidth: 100 },
  { id: 'familyName', label: 'Family', minWidth: 100 },
  { id: 'familyRole', label: 'User Role', minWidth: 100 },
  { id: 'country', label: 'Country', minWidth: 200 },
  { id: 'registeredAt', label: 'Registered', minWidth: 100 },
  { id: 'lastVisitAt', label: 'Last Activity', minWidth: 100 },
  { id: 'deletedAt', label: 'Soft Delete', minWidth: 100 },
  { id: 'hardDelete', label: 'Hard Delete', minWidth: 100 },
  { id: 'email', label: 'Email', minWidth: 100 },
  { id: 'phoneNumber', label: 'Phone Number', minWidth: 100 },
  { id: 'platformType', label: 'Platform Type', minWidth: 100 },
  { id: 'paymentPlatformType', label: 'Plan Type', minWidth: 100 },
];

const columnsUser: readonly Column[] = [
  { id: 'firstName', label: 'First Name', minWidth: 100 },
  { id: 'lastName', label: 'Last Name', minWidth: 100 },
  { id: 'middleName', label: 'Middle Name', minWidth: 100 },
  { id: 'familyName', label: 'Family', minWidth: 100 },
  { id: 'familyRole', label: 'User Role', minWidth: 100 },
  { id: 'country', label: 'Country', minWidth: 200 },
  { id: 'registeredAt', label: 'Registered', minWidth: 100 },
  { id: 'lastVisitAt', label: 'Last Activity', minWidth: 100 },
  { id: 'deletedAt', label: 'Soft Delete', minWidth: 100 },
  { id: 'hardDelete', label: 'Hard Delete', minWidth: 100 },
  { id: 'email', label: 'Email', minWidth: 100 },
  { id: 'phoneNumber', label: 'Phone Number', minWidth: 100 },
  { id: 'platformType', label: 'Platform Type', minWidth: 100 },
  { id: 'paymentPlatformType', label: 'Plan Type', minWidth: 100 },
];

interface IData {
  [key: string]: any;
}

interface UserDataTableProps {
  statisticsData: IData[];
  familyCreator?: boolean;
  totalCount?: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number, rowsPerPage: number) => void;
  onSortChange: (orderBy: string, order: 'asc' | 'desc') => void;
}

export const UserDataTable: React.FC<UserDataTableProps> = ({ statisticsData, familyCreator, totalCount, onPageChange, page, rowsPerPage, onSortChange }) => {
  const [columns, setColumns] = useState(columnsAdmin);

  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('');

  useEffect(() => {
    setColumns(familyCreator ? columnsAdmin : columnsUser);
  }, [familyCreator]);

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = +event.target.value;
    onPageChange(0, newRowsPerPage);
  };

  const handleSortRequest = (columnId: string) => {
    const isAsc = orderBy === columnId && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(columnId);
    onSortChange(columnId, newOrder);
  };

  const getFullCountryName = (
    countryCode: string | undefined,
    phoneNumber: string | undefined,
    country: string | undefined
  ): string => {
    if (!countryCode && !phoneNumber) return '-';
    const fallbackCode = country !== '-' && country ? country : countryCode;
    if (!fallbackCode || !countries.isValid(fallbackCode)) return 'Unknown';
    return countries.getName(fallbackCode, 'en') || 'Unknown';
  };

  const isParsableDate = (value: any): boolean => {
    if (typeof value === 'string') {
      const mmddyyyy = /^\d{2}\/\d{2}\/\d{4}$/;
      if (mmddyyyy.test(value)) return true;

      const parsed = new Date(value);
      return !isNaN(parsed.getTime());
    }

    return value instanceof Date && !isNaN(value.getTime());
  };

  const parseToDate = (value: any): Date | null => {
    if (!value) return null;

    if (typeof value === 'string') {
      const [mm, dd, yyyy] = value.split('/');
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return new Date(`${yyyy}-${mm}-${dd}`);
      }

      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    if (value instanceof Date) return value;

    return null;
  };

  const getComparableValue = (value: any): any => {
    if (isParsableDate(value)) {
      const parsed = parseToDate(value);
      return parsed ? parsed.getTime() : 0;
    }

    return String(value || '').toLowerCase();
  };

  const sortedData = [...statisticsData].sort((a, b) => {
    const aValue = getComparableValue(a[orderBy]);
    const bValue = getComparableValue(b[orderBy]);

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', marginBottom: '20px' }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <StatisticsTableCellWrapper
                  key={column.id}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={() => handleSortRequest(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </StatisticsTableCellWrapper>
              ))}
            </TableRow>
          </TableHead>

          {statisticsData.length !== 0 && (
            <TableBody>
              {sortedData.map((row, rowIndex) => (
                <TableRow key={rowIndex} hover role="checkbox" tabIndex={-1}>
                  {columns.map((column) => {
                    let value = row[column.id];
                    if (column.id === 'country' && row.phoneNumber) {
                      const countryCode = parsePhoneNumberFromString(row.phoneNumber)?.country;
                      value = getFullCountryName(countryCode, row.phoneNumber, row.country);
                    }
                    return (
                      <StaticticsColumWrapper key={column.id} align="left">
                        {value}
                      </StaticticsColumWrapper>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>

        {totalCount === 0 && <NoDataFound />}
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 100]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
